import { EventType } from '@core/models';
import { computed } from 'mobx';
import {
  ExtendedModel,
  _await,
  fromSnapshot,
  getSnapshot,
  idProp,
  model,
  modelFlow,
  prop,
  tProp,
  types,
} from 'mobx-keystone';
import { MainModel } from 'store/utils/base-model';
import { FollowedTrader } from '../leaderboard/leaderboard';
import { formatDate } from 'utils/formatDate';

@model('trade-event')
export class TradeEvent extends ExtendedModel(MainModel, {
  id: idProp,
  createdAt: prop<string>(),
  type: prop<EventType>(),
  symbol: prop<string>(),
  entryPrice: prop<number>(),
  markPrice: prop<number>(),
  pnl: prop<number>(),
  roe: prop<number>(),
  amount: prop<number>(),
  previousAmount: prop<number>(),
  updateTimeStamp: prop<number>(),
  tradeBefore: prop<boolean>(),
  long: prop<boolean>(),
  leverage: prop<number>(),
  trader: tProp(FollowedTrader),
}) {
  @computed
  get amountChange() {
    return Math.abs(this.amount - this.previousAmount);
  }

  @computed
  get percentageChange() {
    const sign = Math.abs(this.amount) > Math.abs(this.previousAmount) ? '' : '-';
    return `${sign}${((this.amountChange / Math.abs(this.previousAmount)) * 100).toFixed(2)}%`;
  }

  @computed
  get formattedDate(){
    return formatDate(this.createdAt);
  }

  @computed
  get formattedRoe() {
    return (super.roe * 100).toFixed(2) + '%';
  }

  @computed
  get isProfitable() {
    return Math.sign(this.roe) === 1;
  }
}

@model('trade-events')
export class TradeEvents extends ExtendedModel(MainModel, {
  events: tProp(types.array(types.model(TradeEvent)), () => []),
  count: tProp(types.number),
  take: tProp(types.number, 25),
  skip: tProp(types.number, 0),
}) {
  @modelFlow
  *fetchEvents() {
    const { take, skip } = this;
    const { data, count } = yield* _await(this.api.fetch('trade-events', {
      take,
      skip,
    }));

    this.events = fromSnapshot([
      ...getSnapshot(this.events),
      ...data,
    ]);

    this.count = count;
    this.skip = this.skip + this.take;
  }

  @computed
  get hasMore() {
    return this.count >= this.events.length;
  }
}
